import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Search, Database, TrendingUp, Filter, ArrowRight, Users, Building2, Trophy, Zap, Lock, Menu, X, Home } from 'lucide-react';
import Footer from '@/components/Footer';
import SEO, { PAGE_SEO } from '@/components/SEO';
import analytics from '@/utils/analytics';

// Use current origin for API calls to work in both preview and production
const API = window.location.origin + '/api';

// Access control thresholds for brands
// Access control thresholds for brands
// Logged-in users get FULL access (no paywall until payment integration)
const BRAND_ACCESS = {
  public: { visible: 6, locked: 4, maxRender: 10 },      // Not logged in: 6 visible, 4 locked, stop at 10
  loggedIn: { visible: 100, locked: 0, maxRender: 100 }, // Logged in: FULL access (no paywall)
  premium: { visible: 100, locked: 0, maxRender: 100 }   // Premium: all visible
};

// Helper to check if brand is recent (within 7 days)
const isRecentBrand = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = (now - date) / (1000 * 60 * 60 * 24);
  return diffDays <= 7;
};

// Helper to format "last seen" time
const formatLastSeen = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffHours = (now - date) / (1000 * 60 * 60);
  
  if (diffHours < 24) return 'Today';
  if (diffHours < 48) return 'Yesterday';
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} days ago`;
  return null;
};

// Helper to get trust signal label based on mention count
const getTrustSignalLabel = (mentionCount) => {
  if (mentionCount >= 3) return 'Frequently featured across videos';
  if (mentionCount === 2) return 'Repeated sponsor';
  return 'Recently detected';
};

// Helper to get sponsor type badge styling
const getSponsorTypeBadge = (sponsorType, sponsorScore, brandType, platformType) => {
  // First check if it's an affiliate platform/network (NOT a sponsor)
  if (brandType === 'affiliate_platform') {
    // Different badges based on platform type
    switch (platformType) {
      case 'affiliate_network':
        return {
          label: 'Affiliate Network',
          icon: '🔗',
          className: 'bg-purple-100 text-purple-700 border-purple-200',
          tooltip: 'Used by creators to promote brands'
        };
      case 'marketplace':
        return {
          label: 'Marketplace',
          icon: '🛒',
          className: 'bg-blue-100 text-blue-700 border-blue-200',
          tooltip: 'E-commerce platform'
        };
      case 'content_platform':
        return {
          label: 'Content Platform',
          icon: '📱',
          className: 'bg-indigo-100 text-indigo-700 border-indigo-200',
          tooltip: 'Content distribution platform'
        };
      case 'link_shortener':
        return {
          label: 'Link Platform',
          icon: '🔗',
          className: 'bg-gray-100 text-gray-600 border-gray-200',
          tooltip: 'Link shortener/bio platform'
        };
      default:
        return {
          label: 'Platform',
          icon: '⚙️',
          className: 'bg-gray-100 text-gray-600 border-gray-200',
          tooltip: 'Not a sponsor'
        };
    }
  }
  
  // Compute sponsor type from score if not provided
  const type = sponsorType || (
    sponsorScore >= 0.6 ? 'confirmed_sponsor' :
    sponsorScore >= 0.3 ? 'likely_sponsor' : 'mention_only'
  );
  
  switch (type) {
    case 'confirmed_sponsor':
      return {
        label: 'Confirmed Sponsor',
        icon: '✅',
        className: 'bg-emerald-100 text-emerald-700 border-emerald-200'
      };
    case 'likely_sponsor':
      return {
        label: 'Likely Sponsor',
        icon: '⚠️',
        className: 'bg-amber-100 text-amber-700 border-amber-200'
      };
    default:
      return {
        label: 'Mentioned',
        icon: 'ℹ️',
        className: 'bg-slate-100 text-slate-600 border-slate-200'
      };
  }
};

const PublicBrandBrowser = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNiche, setSelectedNiche] = useState('all');
  const [selectedSponsorType, setSelectedSponsorType] = useState('all'); // NEW: sponsor type filter
  const [availableNiches, setAvailableNiches] = useState([]);
  const [totalBrands, setTotalBrands] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [whatsNewCount, setWhatsNewCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const limit = 12;

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data && response.data.user_id) {
          setIsLoggedIn(true);
          setIsPremium(response.data.is_premium || false);
        }
      } catch (error) {
        setIsLoggedIn(false);
        setIsPremium(false);
      }
    };
    checkAuth();
  }, []);

  // Compute access thresholds based on auth state (deterministic, no flicker)
  const accessLevel = isPremium ? 'premium' : (isLoggedIn ? 'loggedIn' : 'public');
  const { visible: visibleCount, locked: lockedCount, maxRender } = BRAND_ACCESS[accessLevel];

  // Fetch "What's New" count for navbar
  useEffect(() => {
    const fetchWhatsNewCount = async () => {
      try {
        const res = await axios.get(`${API}/data/whats-new-count`);
        setWhatsNewCount(res.data?.count || 0);
      } catch (error) {
        // Silently fail - not critical
      }
    };
    fetchWhatsNewCount();
  }, []);

  const loadBrands = useCallback(async (pageNum = 1, reset = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: ((pageNum - 1) * limit).toString(),
        sort_by: 'mention_count'
      });
      
      if (selectedNiche && selectedNiche !== 'all') {
        params.append('niche', selectedNiche);
      }
      
      // NEW: Add sponsor type filter
      if (selectedSponsorType && selectedSponsorType !== 'all') {
        params.append('sponsor_type', selectedSponsorType);
      }
      
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      const response = await axios.get(`${API}/admin/brands?${params.toString()}`);
      const data = response.data;
      
      if (reset || pageNum === 1) {
        setBrands(data.brands || []);
      } else {
        setBrands(prev => [...prev, ...(data.brands || [])]);
      }
      
      setTotalBrands(data.total || 0);
      setAvailableNiches(data.available_niches || []);
      setHasMore((data.brands || []).length === limit);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to load brands:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedNiche, searchQuery, selectedSponsorType]);

  useEffect(() => {
    loadBrands(1, true);
  }, [selectedNiche, selectedSponsorType]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadBrands(1, true);
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadBrands(page + 1);
    }
  };

  const getConfidenceColor = (confidence) => {
    const pct = confidence * 100;
    if (pct >= 80) return 'bg-emerald-100 text-emerald-700';
    if (pct >= 60) return 'bg-blue-100 text-blue-700';
    if (pct >= 40) return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700';
  };

  const handleAnalyzeChannel = () => {
    navigate('/');
    setTimeout(() => {
      const input = document.querySelector('[data-testid="youtube-link-input"]');
      if (input) input.focus();
    }, 100);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Meta Tags */}
      <SEO {...PAGE_SEO.exploreBrands} />
      
      {/* Navigation - Responsive */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo-link">
            <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
            <span className="text-lg md:text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm" className="text-indigo-600 bg-indigo-50">
                <Database className="w-4 h-4 mr-1.5" />
                Discover Brands
              </Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="ghost" size="sm">
                <Users className="w-4 h-4 mr-1.5" />
                Discover Creators
              </Button>
            </Link>
            {whatsNewCount > 0 && (
              <Link to="/new-this-week">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-emerald-700 hover:bg-emerald-50">
                  <Zap className="w-4 h-4 mr-1" />
                  <Badge className="ml-1 bg-emerald-100 text-emerald-700 text-xs font-medium px-1.5 py-0 h-5">
                    {whatsNewCount > 99 ? '99+' : whatsNewCount}
                  </Badge>
                </Button>
              </Link>
            )}
            <Link to="/leaderboards">
              <Button variant="ghost" size="sm">
                <Trophy className="w-4 h-4 mr-1.5" />
                Leaderboards
              </Button>
            </Link>
            <Link to="/">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
                Analyze Sponsorships
              </Button>
            </Link>
          </div>
          
          {/* Mobile Navigation Controls */}
          <div className="flex md:hidden items-center gap-2">
            <Link to="/">
              <Button variant="ghost" size="sm" className="h-8 px-2">
                <Home className="w-4 h-4" />
              </Button>
            </Link>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-slate-700" />
              ) : (
                <Menu className="w-5 h-5 text-slate-700" />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              <Link 
                to="/" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Home className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Home</span>
              </Link>
              <Link 
                to="/explore-brands" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-indigo-50 text-indigo-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Database className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Discover Brands</span>
                <Badge className="ml-auto bg-indigo-100 text-indigo-700 text-xs">Current</Badge>
              </Link>
              <Link 
                to="/brands/discover" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Users className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Discover Creators</span>
              </Link>
              <Link 
                to="/leaderboards" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Trophy className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">Leaderboards</span>
              </Link>
              {whatsNewCount > 0 && (
                <Link 
                  to="/new-this-week" 
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Zap className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium">What's New</span>
                  <Badge className="ml-auto bg-emerald-100 text-emerald-700 text-xs">
                    {whatsNewCount > 99 ? '99+' : whatsNewCount}
                  </Badge>
                </Link>
              )}
              <Link 
                to="/brands/login" 
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-slate-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Building2 className="w-4 h-4 text-indigo-600" />
                <span className="font-medium">For Brands</span>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-indigo-50 to-slate-50 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-4">
            <Database className="w-4 h-4" />
            <span>Sponsorship Intelligence Database</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
            Discover Active Sponsors
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
            Browse brands actively sponsoring YouTube creators. Find sponsors in your niche.
          </p>
          
          {/* Freshness indicator */}
          {whatsNewCount > 0 && (
            <p className="text-sm text-emerald-600 mb-6">
              <Zap className="w-4 h-4 inline mr-1" />
              New sponsors added this week
            </p>
          )}
          
          {/* Search and Filters */}
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSearch} className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search brands by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 bg-white"
                  data-testid="brand-search-input"
                />
              </div>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white h-12 px-6">
                Search
              </Button>
            </form>
            
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Filter className="w-4 h-4 text-slate-500" />
              
              {/* Sponsor Type Filter */}
              <Select value={selectedSponsorType} onValueChange={setSelectedSponsorType}>
                <SelectTrigger className="w-44 bg-white" data-testid="sponsor-type-filter">
                  <SelectValue placeholder="All Sponsors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sponsors</SelectItem>
                  <SelectItem value="confirmed_sponsor">
                    <span className="flex items-center gap-1.5">
                      <span>✅</span> Confirmed
                    </span>
                  </SelectItem>
                  <SelectItem value="likely_sponsor">
                    <span className="flex items-center gap-1.5">
                      <span>⚠️</span> Likely
                    </span>
                  </SelectItem>
                  <SelectItem value="mention_only">
                    <span className="flex items-center gap-1.5">
                      <span>ℹ️</span> Mentions Only
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {/* Niche Filter */}
              <Select value={selectedNiche} onValueChange={setSelectedNiche}>
                <SelectTrigger className="w-48 bg-white" data-testid="niche-filter">
                  <SelectValue placeholder="All Niches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Niches</SelectItem>
                  {availableNiches.map((niche) => (
                    <SelectItem key={niche} value={niche}>{niche}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-500">
                {totalBrands} brands tracked
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {loading && brands.length === 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1,2,3,4,5,6,7,8].map(i => (
                <Card key={i} className="p-6 animate-pulse">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
                  <div className="h-8 bg-slate-200 rounded mb-3"></div>
                  <div className="h-10 bg-slate-200 rounded"></div>
                </Card>
              ))}
            </div>
          ) : brands.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No brands found</h3>
              <p className="text-slate-600">Try adjusting your search or filter criteria.</p>
            </div>
          ) : (
            <>
              {/* Subtle value message */}
              <p className="text-sm text-slate-500 text-center mb-6">
                Creators like you are working with these brands
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {/* Slice brands BEFORE render for performance - backend returns full dataset */}
                {brands.slice(0, maxRender).map((brand, index) => {
                  // Determine card state: visible, locked, or hidden
                  const isLocked = index >= visibleCount;
                  // CTA ONLY at index 6 (first locked item for public users)
                  const showSignInCTA = !isLoggedIn && index === 6;
                  // Upgrade CTA ONLY at first locked item for logged-in free users
                  const showUpgradeCTA = isLoggedIn && !isPremium && index === BRAND_ACCESS.loggedIn.visible;
                  
                  return (
                    <Card 
                      key={brand.detected_brand_id || index} 
                      className={`p-6 transition-shadow bg-white relative ${
                        isRecentBrand(brand.first_detected_at) ? 'ring-1 ring-emerald-200' : ''
                      } ${isLocked ? 'cursor-not-allowed' : 'hover:shadow-lg cursor-pointer'}`}
                      style={isLocked ? { 
                        filter: 'blur(4px)', 
                        opacity: 0.6,
                        pointerEvents: 'none'
                      } : {}}
                      tabIndex={isLocked ? -1 : 0}
                      data-testid={`brand-card-${index}`}
                      onClick={isLocked ? undefined : () => {
                        analytics.brandClick(brand.brand_name, brand.detected_brand_id, 'explore_brands');
                        navigate(`/brand/${encodeURIComponent(brand.brand_name)}`);
                      }}
                      onKeyDown={isLocked ? undefined : (e) => {
                        if (e.key === 'Enter') {
                          analytics.brandClick(brand.brand_name, brand.detected_brand_id, 'explore_brands');
                          navigate(`/brand/${encodeURIComponent(brand.brand_name)}`);
                        }
                      }}
                    >
                      {/* Sign-in CTA - ONLY at index 6 */}
                      {showSignInCTA && (
                        <div 
                          className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center z-10"
                          style={{ filter: 'none', pointerEvents: 'auto' }}
                        >
                          <Lock className="w-8 h-8 text-indigo-600 mb-3" />
                          <p className="text-sm text-slate-700 text-center px-4 mb-3 font-medium">
                            Sign in to see all brands sponsoring creators
                          </p>
                          <Button 
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/login?redirect=/explore-brands');
                            }}
                          >
                            Sign in
                          </Button>
                        </div>
                      )}
                      
                      {/* Upgrade CTA - ONLY at first locked item for logged-in users */}
                      {showUpgradeCTA && (
                        <div 
                          className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center z-10"
                          style={{ filter: 'none', pointerEvents: 'auto' }}
                        >
                          <Lock className="w-8 h-8 text-indigo-600 mb-3" />
                          <p className="text-sm text-slate-700 text-center px-4 mb-3 font-medium">
                            Unlock full sponsorship database
                          </p>
                          <Button 
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/dashboard');
                            }}
                          >
                            Upgrade
                          </Button>
                        </div>
                      )}
                    
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                              {brand.brand_name}
                            </h3>
                            {/* Sponsor Type Badge */}
                            {(() => {
                              const badge = getSponsorTypeBadge(
                                brand.sponsor_type, 
                                brand.sponsor_confidence_score || brand.detection_confidence,
                                brand.brand_type,
                                brand.platform_type
                              );
                              return (
                                <span 
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${badge.className}`}
                                  title={badge.tooltip || ''}
                                >
                                  <span>{badge.icon}</span>
                                  <span>{badge.label}</span>
                                </span>
                              );
                            })()}
                            {isRecentBrand(brand.first_detected_at) && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">New</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">{brand.niche_category || 'General'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-indigo-600" />
                        <span className="text-2xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                          {brand.mention_count || 0}
                        </span>
                        <span className="text-sm text-slate-600">video mentions</span>
                      </div>
                      
                      {/* Data source proof */}
                      <p className="text-xs text-slate-500 mb-2">Detected from video mentions</p>
                      
                      {/* Timestamp proof - prominent */}
                      {formatLastSeen(brand.last_detected_at) && (
                        <p className="text-xs text-slate-600 mb-2">
                          Last detected {formatLastSeen(brand.last_detected_at)}
                        </p>
                      )}
                      
                      {/* Relationship insight */}
                      {brand.detected_from_channels && brand.detected_from_channels.length > 0 && (
                        <p className="text-xs text-indigo-600 font-medium mb-2">
                          Seen across {brand.detected_from_channels.length} creator{brand.detected_from_channels.length > 1 ? 's' : ''}
                        </p>
                      )}
                      
                      {/* Trust signal label */}
                      <p className="text-xs text-slate-500 mb-3">
                        {getTrustSignalLabel(brand.mention_count || 0)}
                      </p>
                      
                      {/* Example video link - hide for locked */}
                      {!isLocked && brand.sample_videos && brand.sample_videos[0] && (
                        <a 
                          href={`https://www.youtube.com/results?search_query=${encodeURIComponent(brand.sample_videos[0] + ' ' + (brand.detected_from_channels?.[0] || ''))}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-slate-500 hover:text-indigo-600 mb-3 inline-block"
                        >
                          View example video →
                        </a>
                      )}
                      
                      {/* Button - disabled and non-interactive for locked items */}
                      <Button 
                        onClick={isLocked ? undefined : (e) => {
                          e.stopPropagation();
                          analytics.brandClick(brand.brand_name, brand.detected_brand_id, 'explore_brands');
                          navigate(`/brand/${encodeURIComponent(brand.brand_name)}`);
                        }}
                        variant="outline" 
                        className={`w-full ${isLocked ? 'text-slate-400 border-slate-200 cursor-not-allowed' : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50'}`}
                        disabled={isLocked}
                        tabIndex={isLocked ? -1 : 0}
                        data-testid={`view-brand-btn-${index}`}
                      >
                        View Sponsored Creators
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Card>
                  );
                })}
              </div>
              
              {/* Load More - only show if user has access and there's more data */}
              {hasMore && brands.length >= maxRender && !isLoggedIn && (
                <div className="text-center mt-8">
                  <p className="text-slate-600 mb-4">Sign in to discover more brands</p>
                  <Button 
                    onClick={() => navigate('/login?redirect=/explore-brands')}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8"
                  >
                    Sign in to see all brands
                  </Button>
                </div>
              )}
              
              {hasMore && isLoggedIn && brands.length < maxRender && (
                <div className="text-center mt-8">
                  <Button 
                    onClick={handleLoadMore}
                    disabled={loading}
                    variant="outline"
                    className="px-8"
                    data-testid="load-more-btn"
                  >
                    {loading ? 'Loading...' : 'Load More Brands'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Brand Disclaimer */}
      <section className="py-8 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Brand Disclaimer</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Sponsiq analyzes publicly available creator content to detect brand sponsorships. Brand names displayed 
              on this platform are derived from publicly available sponsorship mentions and are shown for informational 
              purposes only. The presence of a brand on Sponsiq does not imply any partnership, endorsement, or 
              affiliation with Sponsiq.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-indigo-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{fontFamily: 'Manrope'}}>
            Ready to Find Your Sponsors?
          </h2>
          <p className="text-indigo-100 mb-8 text-lg">
            Analyze your YouTube channel and discover brands actively sponsoring creators in your niche.
          </p>
          <Link to="/">
            <Button className="bg-white text-indigo-600 hover:bg-indigo-50 h-12 px-8 font-semibold" data-testid="analyze-cta-btn">
              Get Your Sponsorship Report
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* SEO Content Section - For Search Engines */}
      <section className="bg-white border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* SEO-friendly H2 and description - visible on page load */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
              Brands Sponsoring YouTube Creators
            </h2>
            <p className="text-slate-600 max-w-3xl leading-relaxed">
              Discover which brands are actively investing in YouTube sponsorships. Sponsiq tracks sponsorship mentions across thousands of creator videos 
              to build the most comprehensive database of brands partnering with influencers. Whether you're a creator looking for sponsorship opportunities 
              or a marketer researching competitor activity, our brand intelligence helps you make data-driven decisions.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* How It Works */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                How We Track Sponsors
              </h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• AI-powered video content analysis</li>
                <li>• Detection of brand mentions and disclosures</li>
                <li>• Tracking across multiple creator niches</li>
                <li>• Confidence scoring for accuracy</li>
              </ul>
            </div>
            
            {/* Explore More */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Find YouTube Sponsors
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/brands/discover" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Discover Creators Getting Sponsored
                  </Link>
                </li>
                <li>
                  <Link to="/leaderboards" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Top Creator Leaderboards
                  </Link>
                </li>
                <li>
                  <Link to="/" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Get Your Sponsorship Report
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Value Proposition */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Influencer Sponsorship Marketplace
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Sponsiq is the go-to platform for brands looking to find YouTube sponsors and creators seeking brand partnership opportunities. 
                Our marketplace intelligence helps you understand the sponsorship landscape before making outreach decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer showDisclaimer={true} />
    </div>
  );
};

export default PublicBrandBrowser;
